/**
 * Recherche prédictive — module « requête ».
 *
 * Responsabilité unique : parler au réseau. Construction de l'URL de suggestion,
 * debounce fonctionnel, annulation de la requête en vol, parsing de la réponse et
 * cache par terme. Ce module ne touche jamais au DOM du panneau : il rend ce que le
 * réseau a répondu, l'élément hôte décide quoi en faire.
 *
 * S'enregistre sous `window.MintPredictiveSearch.fetcher`, chargé en `<script defer>`
 * AVANT `predictive-search.js` — l'ordre du document fait l'ordre d'exécution des
 * `defer`, c'est le contrat de chargement de la famille `buyback-portal-*`.
 *
 * Contrat : `create(options)` rend un objet de collaboration, ou `null` si une option
 * requise manque — jamais un objet à moitié monté. L'état de la requête en vol vit dans
 * `state`, et `abort()` / `destroy()` en sont les seuls consommateurs.
 *
 * `search(terme)` rend une `Promise`, une seule vivante par instance : résolue avec
 * `{ term, url, sectionId, html, doc, panel, fromCache }` ; résolue avec `null` sur un terme
 * vide, AUCUNE requête ne partant alors (`/search/suggest` répond 422 sans `q`, mesuré) ;
 * rejetée avec `name === 'AbortError'` à chaque annulation — nouvelle frappe, `abort()`,
 * `destroy()` — qui est un fonctionnement normal, jamais un message montré au visiteur
 * (l'appelant filtre comme `assets/homepage-banner-finder.js`) ; rejetée avec une `Error`
 * portant `status` sur une réponse HTTP non `ok`. Tout appel annule le précédent : l'appelant
 * DOIT poser un `.catch()`, sinon la frappe suivante produit une rejection non gérée.
 *
 * Ce module ne sait PAS distinguer une frappe d'un `input` programmatique : il part dès qu'on
 * l'appelle avec un terme non vide. Le garde « ne rien déclencher au chargement » — le champ
 * est pré-rempli sur `/search?q=…` (`value="{{ search.terms … }}"`) — appartient donc à l'hôte.
 *
 * Complété par : T21 (normalisation du terme, verrou après erreur), T22 (chargement, état vide,
 * erreur), T40 (`section_id` et contexte FMD sur le document détaché), T48 (limite mesurée).
 *
 * Chargé en asset séparé, jamais en bloc `javascript` de section : raison écrite en
 * tête de `predictive-search.js`.
 */
(function (window) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  /** Debounce entre deux frappes, en millisecondes. Distinct du debounce de tracking (800 ms). */
  var DEBOUNCE_MS = 250;

  /** Réponses gardées en mémoire, clé = URL complète. Plafond repris du patron `theme.AjaxRenderer`. */
  var CACHE_MAX_ENTRIES = 20;

  /**
   * Section demandée par défaut. Le choix entre `predictive-search` et `predictive-search-fmd`
   * appartient à T40 et se passe en option : ce littéral n'est que le repli standard. Il ne se
   * déduit JAMAIS de l'URL de la page — canal contaminé par le cache Shopify (10 réponses sur
   * 31 servies avec le mauvais contexte, mesuré) ; le `section_id` porte l'information.
   */
  var DEFAULT_SECTION_ID = 'predictive-search';

  /**
   * Section FMD (T40) : jumelle littérale de `sections/predictive-search-fmd.liquid`.
   * Choisie côté client par `predictive-search.js` (`resolveFmdSectionId`) et transmise en
   * `sectionId` d'option — jamais déduite ici de l'URL. Ce fichier ne fait qu'agir dessus,
   * une fois le choix fait ailleurs.
   */
  var FMD_SECTION_ID = 'predictive-search-fmd';

  /** Racine du loyer FMD dans une réponse de section (`snippets/predictive-search-panel.liquid`). */
  var RENTAL_SELECTOR = 'greenleaze-rental-options';

  /**
   * Cadrage demandé au serveur, PAR TYPE de ressource (`limit_scope=each`). Repli sur le `default`
   * du réglage `predictive_resources_limit` (`config/settings_schema.json`, groupe « Search »), qui
   * n'a pas encore de porteur dans `theme.settings` : le jour où le layout l'expose sous
   * `predictiveResourcesLimit`, il est pris sans toucher à ce fichier. Bornes du même réglage — à
   * 10 on demande jusqu'à 30 ressources pour en afficher au mieux 16, ce que T48 mesure.
   */
  var DEFAULT_RESOURCES_LIMIT = 6;
  var MIN_RESOURCES_LIMIT = 1;
  var MAX_RESOURCES_LIMIT = 10;

  /**
   * `article` retire le 2026-09-16 (decision utilisateur) : le panneau n affiche plus le bloc
   * « Articles et guides ». On cesse donc aussi de les DEMANDER — les laisser dans la requete
   * ferait payer au serveur et au reseau un contenu que personne ne rend.
   */
  var RESOURCE_TYPES = 'product,collection';
  /** `body` est exclu à dessein : le panneau n'en affiche rien, et il pèse sur la réponse. */
  var RESOURCE_FIELDS = 'title,product_type,variants.title,vendor';
  /** Repli de route, pour un contexte où `theme.routes` n'a pas été rendu. */
  var SUGGEST_PATH_FALLBACK = '/search/suggest';
  /** Racine du panneau dans la réponse de section (`snippets/predictive-search-panel.liquid`). */
  var PANEL_SELECTOR = '[data-predictive-panel]';

  /** Annulation, de la forme d'un `fetch` avorté : c'est `name` qui fait foi, pas la classe. */
  function abortError() {
    var error = new Error('Recherche prédictive : requête annulée.');
    error.name = 'AbortError';
    return error;
  }

  /** Route rendue en Liquid : un `/search/suggest` en dur casserait sur un préfixe de locale. */
  function suggestPath() {
    var routes = (window.theme && window.theme.routes) || {};
    return routes.predictiveSearch || SUGGEST_PATH_FALLBACK;
  }

  function resolveLimit(value) {
    var settings = (window.theme && window.theme.settings) || {};
    var candidates = [value, settings.predictiveResourcesLimit, DEFAULT_RESOURCES_LIMIT];
    for (var i = 0; i < candidates.length; i++) {
      var parsed = parseInt(candidates[i], 10);
      if (!isNaN(parsed)) return Math.min(Math.max(parsed, MIN_RESOURCES_LIMIT), MAX_RESOURCES_LIMIT);
    }
    return DEFAULT_RESOURCES_LIMIT;
  }

  /**
   * URL de suggestion. **Aucun paramètre FMD n'y figure, et il ne faut jamais en ajouter** :
   * Shopify met la réponse en cache avec une clé qui ignore les paramètres qu'il ne connaît pas,
   * donc un `fmd=` y serait ignoré du cache et servirait le contexte d'un autre visiteur. Le
   * contexte FMD passe par le `section_id`, choisi côté client (T40).
   *
   * `unavailable_products=last` — NÉCESSAIRE MAIS INSUFFISANT. **Garder les deux étages.**
   *
   * `last` place les indisponibles après les produits en stock, sans les retirer : c'est ce qui
   * laisse passer un vélo réservé (indisponible jusqu'à l'échéance de sa réservation). `hide`
   * l'écarterait côté serveur. Le second étage, dans `snippets/predictive-search-panel.liquid`,
   * ne garde que les produits en stock et les réservés ; les vendus n'apparaissent que s'il n'y a rien d'autre.
   *
   * ⚠️ **Ne passe pas à `show`** : le serveur renverrait 4 à 6 vendus sur 6, le filtre Liquid
   * les écarterait tous, et le panneau n'afficherait plus que 0 à 2 produits, sans qu'aucune
   * erreur ne le signale.
   *
   * Encodage au patron maison (`paramUrl`, `assets/theme.js`) : clé brute, valeur encodée — la
   * forme que le legacy envoie en production depuis toujours, l'encodage des crochets n'ayant
   * pas été mesuré. `encodeURIComponent` et non `URLSearchParams`, qui encode l'espace en `+`
   * là où « vtt electrique » doit partir en `vtt%20electrique`.
   */
  function buildUrl(term, sectionId, limit) {
    var pairs = [
      ['q', term],
      ['section_id', sectionId],
      ['resources[type]', RESOURCE_TYPES],
      ['resources[limit]', limit],
      ['resources[limit_scope]', 'each'],
      ['resources[options][unavailable_products]', 'last'],
      ['resources[options][fields]', RESOURCE_FIELDS],
    ];
    var query = pairs
      .map(function (pair) {
        return pair[0] + '=' + encodeURIComponent(pair[1]);
      })
      .join('&');
    return suggestPath() + '?' + query;
  }

  /**
   * `DOMParser`, jamais `innerHTML` : patron de `theme.Recommendations` (`assets/theme.js`,
   * `?section_id=product-recommendations&limit=`), et c'est lui qui rend possible le patch du
   * contexte FMD sur le document DÉTACHÉ, avant insertion (T40) — rien d'intermédiaire n'est
   * jamais peint.
   */
  function parseResponse(html) {
    var doc = new window.DOMParser().parseFromString(html, 'text/html');
    return { doc: doc, panel: doc.querySelector(PANEL_SELECTOR) };
  }

  /**
   * Recopie le contexte de remise partenaire (T39, `layout/theme.liquid`,
   * `#FmdPanelContext`) sur les `<greenleaze-rental-options>` du document DÉTACHÉ produit
   * par `parseResponse`, AVANT toute insertion — l'appelant (`predictive-search-states.js`)
   * ne fait qu'un `appendChild` du `panel` déjà rendu ici, donc rien d'intermédiaire n'est
   * jamais peint (méthode des anti-patterns n°14/19 : corriger un nœud APRÈS insertion se
   * peint avec ses valeurs vides).
   * Lu depuis le document HÔTE (`window.document`), jamais depuis `doc` : le script publié
   * par le layout vit dans le `<head>` de la page, jamais dans une réponse de section.
   * Miroir exact de `snippets/product-grid-item.liquid` (ancre
   * `assign metaobject = metaobjects.partner_companies[fmd]`) : même mapping de champs,
   * pour que la mensualité du panneau ne diverge jamais de celle de la grille.
   * Absent, illisible, ou parcours générique (aucun `#FmdPanelContext` publié par
   * construction — T39 le garde à `fmd != blank`) : les attributs restent VIDES tels que
   * rendus par `predictive-search-panel.liquid`. Repli sûr, jamais un montant deviné ;
   * `greenleaze-rental-options.js` retombe alors sur `localStorage['fmd-data']` (flux
   * générique), sans aucun aller-retour serveur.
   */
  function applyFmdContext(doc) {
    var nodes = doc.querySelectorAll(RENTAL_SELECTOR);
    if (!nodes.length) return;

    var scriptNode = window.document.getElementById('FmdPanelContext');
    if (!scriptNode) return;

    var context;
    try {
      context = JSON.parse(scriptNode.textContent);
    } catch (error) {
      return;
    }

    var discountValue = context.type_de_reduction === 'fixed' ? context.montant : context.pourcentage;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (context.type_de_reduction != null) node.setAttribute('data-discount-type', context.type_de_reduction);
      if (discountValue != null) node.setAttribute('data-discount-value', discountValue);
      if (context.plafond_remboursement_mensuel != null) {
        node.setAttribute('data-fmd-cap', context.plafond_remboursement_mensuel);
      }
      if (context.reduction_fmd === true) node.setAttribute('data-reduction-fmd', 'true');
    }
  }

  /**
   * @param {{ location: string, sectionId?: string, resourcesLimit?: number }} options -
   *   `location` vient de `data-predictive-location`, unique par instance : il sert de
   *   discriminant dans les events et les clés d'état.
   * @returns {object|null}
   */
  function createFetcher(options) {
    var config = options || {};
    if (!config.location) return null;

    var sectionId = config.sectionId || DEFAULT_SECTION_ID;
    var resourcesLimit = resolveLimit(config.resourcesLimit);

    var state = {
      controller: null,
      timer: null,
      cache: new Map(),
      /** `reject` de la promesse en attente, à appeler dès qu'une annulation survient. */
      pending: null,
      /**
       * Dernier terme réellement parti au réseau ou servi du cache. Purement
       * observable (le fetcher expose `state`) : aucune décision du moteur ne s'y
       * appuie. La dé-duplication « même terme, on ne repart pas » vit dans
       * `predictive-search-states.js` et lit SON propre `state.term`.
       */
      term: '',
    };

    /** Annule la requête en vol et le debounce en attente. Idempotent. */
    function abort() {
      if (state.controller) {
        state.controller.abort();
        state.controller = null;
      }
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }

      var pending = state.pending;
      state.pending = null;
      if (pending) pending(abortError());
    }

    function destroy() {
      abort();
      state.cache.clear();
    }

    /**
     * Lecture du cache, qui remonte l'entrée servie en queue d'éviction : le terme qu'on
     * vient de retaper est justement celui qu'il ne faut pas jeter en premier.
     */
    function readCache(url) {
      if (!state.cache.has(url)) return null;
      var html = state.cache.get(url);
      state.cache.delete(url);
      state.cache.set(url, html);
      return html;
    }

    function writeCache(url, html) {
      state.cache.delete(url);
      state.cache.set(url, html);
      while (state.cache.size > CACHE_MAX_ENTRIES) {
        state.cache.delete(state.cache.keys().next().value);
      }
    }

    /**
     * Le cache garde la CHAÎNE, jamais le document parsé — c'est structurel : l'appelant insère
     * `panel` par `appendChild`, ce qui le DÉPLACE hors du document détaché. Une entrée qui
     * aurait gardé son `doc` serait vidée par la première insertion, et le backspace rendrait un
     * panneau vide. On re-parse : chaque service est indépendant (T40 repatche à chaque fois).
     */
    function toResult(term, url, target, html, fromCache) {
      var parsed = parseResponse(html);
      /** AVANT tout retour à l'appelant : lui n'insère que ce qui est déjà patché. */
      if (target === FMD_SECTION_ID) applyFmdContext(parsed.doc);
      return {
        term: term,
        url: url,
        sectionId: target,
        html: html,
        doc: parsed.doc,
        panel: parsed.panel,
        fromCache: fromCache,
      };
    }

    /**
     * Une frappe = une intention. L'appel annule la précédente (requête en vol comprise), puis
     * attend `DEBOUNCE_MS` avant de partir ; le cache, lui, répond sans réseau, ce qui rend le
     * backspace et les reformulations gratuits. La clé de cache est l'URL complète, donc le
     * `section_id` en fait partie — un terme lu en standard ne sert jamais l'entrée FMD.
     */
    function search(term, searchOptions) {
      var settings = searchOptions || {};
      /**
       * Normalisation portée du legacy (T21) : `trim()` + `toLowerCase()`, SANS
       * transformation des espaces. `_normalizeQuery` (`assets/theme.js`) faisait
       * `replace(/\ /g, '-')` et envoyait « vtt electrique » en `vtt-electrique` — ne
       * JAMAIS réintroduire ce remplacement.
       */
      var query = String(term == null ? '' : term)
        .trim()
        .toLowerCase();

      abort();
      if (!query) return Promise.resolve(null);

      var target = settings.sectionId || sectionId;
      var url = buildUrl(query, target, resourcesLimit);
      state.term = query;

      var cached = readCache(url);
      if (cached !== null) return Promise.resolve(toResult(query, url, target, cached, true));

      return new Promise(function (resolve, reject) {
        state.pending = reject;

        state.timer = setTimeout(function () {
          state.timer = null;

          var controller = new AbortController();
          state.controller = controller;

          window
            .fetch(url, { signal: controller.signal })
            .then(function (response) {
              if (!response.ok) {
                var error = new Error('Recherche prédictive : réponse ' + response.status + '.');
                error.status = response.status;
                throw error;
              }
              /** La réponse est du HTML de section : `.text()`, jamais `.json()`. */
              return response.text();
            })
            .then(function (html) {
              /** Une annulation ou une frappe plus récente a pris la main : cette réponse est périmée. */
              if (state.controller !== controller) return;
              state.controller = null;
              state.pending = null;
              writeCache(url, html);
              resolve(toResult(query, url, target, html, false));
            })
            .catch(function (error) {
              if (state.controller !== controller) return;
              state.controller = null;
              state.pending = null;
              /**
               * Reset du terme, en plus du relâchement du verrou ci-dessus (T21,
               * correctif n°2) : `state.term` est posé AVANT le fetch, donc sans ceci
               * le fetcher resterait à se dire en train de chercher un terme qu'il a
               * abandonné. C'est une remise en cohérence de son état observable, PAS
               * le correctif du bug legacy : ici rien ne relit `state.term`, et la
               * garde « même terme, on ne repart pas » qui swallowait la re-frappe
               * vit dans `predictive-search-states.js`, dont le `fail()` remet bien
               * son propre terme à zéro. Ne pas déduire de ce reset qu'une garde
               * existe dans ce fichier — elle n'y est pas.
               */
              state.term = '';
              reject(error);
            });
        }, DEBOUNCE_MS);
      });
    }

    return {
      location: config.location,
      sectionId: sectionId,
      debounceMs: DEBOUNCE_MS,
      cacheLimit: CACHE_MAX_ENTRIES,
      resourcesLimit: resourcesLimit,
      state: state,
      /** Exposé pour les mesures : c'est sur lui que se vérifie l'absence de paramètre FMD. */
      buildUrl: function (term, overrideSectionId) {
        return buildUrl(term, overrideSectionId || sectionId, resourcesLimit);
      },
      search: search,
      abort: abort,
      destroy: destroy,
    };
  }

  registry.fetcher = { create: createFetcher };
})(window);
